import React, { useContext } from 'react';

import {
  alpha,
  Box,
  Button,
  List,
  ListItem,
  ListSubheader,
  styled
} from '@mui/material';
import { NavLink as RouterLink } from 'react-router-dom';
import { SidebarContext } from 'src/contexts/SidebarContext';
import BrightnessLowTwoToneIcon from '@mui/icons-material/BrightnessLowTwoTone';
import TableChartTwoToneIcon from '@mui/icons-material/TableChartTwoTone';
import ConfirmationNumberTwoToneIcon from '@mui/icons-material/ConfirmationNumberTwoTone';
import { FormattedMessage } from 'react-intl';
import { UserContext } from '../../../../contexts/userContext';
import { UserType } from '../../../../interfaces/UserTypes';
import { ProductIdContext } from '../../../../contexts/ProductIdContextProvider';

const MenuWrapper = styled(Box)(
  ({ theme }) => `
  .MuiList-root {
    padding: ${theme.spacing(1)};

    & > .MuiList-root {
      padding: 0 ${theme.spacing(0)} ${theme.spacing(1)};
    }
  }

    .MuiListSubheader-root {
      text-transform: uppercase;
      font-weight: bold;
      font-size: ${theme.typography.pxToRem(12)};
      color: ${theme.colors.alpha.trueWhite[50]};
      padding: ${theme.spacing(0, 2.5)};
      line-height: 1.4;
    }
`
);

const SubMenuWrapper = styled(Box)(
  ({ theme }) => `
    .MuiList-root {

      .MuiListItem-root {
        padding: 1px 0;

        .MuiBadge-root {
          position: absolute;
          right: ${theme.spacing(3.2)};

          .MuiBadge-standard {
            background: ${theme.colors.primary.main};
            font-size: ${theme.typography.pxToRem(10)};
            font-weight: bold;
            text-transform: uppercase;
            color: ${theme.palette.primary.contrastText};
          }
        }
    
        .MuiButton-root {
          display: flex;
          color: ${theme.colors.alpha.trueWhite[70]};
          background-color: transparent;
          width: 100%;
          justify-content: flex-start;
          padding: ${theme.spacing(1.2, 3)};

          .MuiButton-startIcon,
          .MuiButton-endIcon {
            transition: ${theme.transitions.create(['color'])};

            .MuiSvgIcon-root {
              font-size: inherit;
              transition: none;
            }
          }

          .MuiButton-startIcon {
            color: ${theme.colors.alpha.trueWhite[30]};
            font-size: ${theme.typography.pxToRem(20)};
            margin-right: ${theme.spacing(1)};
          }
          
          .MuiButton-endIcon {
            color: ${theme.colors.alpha.trueWhite[50]};
            margin-left: auto;
            opacity: .8;
            font-size: ${theme.typography.pxToRem(20)};
          }

          &.active,
          &:hover {
            background-color: ${alpha(theme.colors.alpha.trueWhite[100], 0.06)};
            color: ${theme.colors.alpha.trueWhite[100]};

            .MuiButton-startIcon,
            .MuiButton-endIcon {
              color: ${theme.colors.alpha.trueWhite[100]};
            }
          }
        }

        &.Mui-children {
          flex-direction: column;

          .MuiBadge-root {
            position: absolute;
            right: ${theme.spacing(7)};
          }
        }

        .MuiCollapse-root {
          width: 100%;

          .MuiList-root {
            padding: ${theme.spacing(1, 0)};
          }

          .MuiListItem-root {
            padding: 1px 0;

            .MuiButton-root {
              padding: ${theme.spacing(0.8, 3)};

              .MuiBadge-root {
                right: ${theme.spacing(3.2)};
              }

              &:before {
                content: ' ';
                background: ${theme.colors.alpha.trueWhite[100]};
                opacity: 0;
                transition: ${theme.transitions.create([
                  'transform',
                  'opacity'
                ])};
                width: 6px;
                height: 6px;
                transform: scale(0);
                transform-origin: center;
                border-radius: 20px;
                margin-right: ${theme.spacing(1.8)};
              }

              &.active,
              &:hover {

                &:before {
                  transform: scale(1);
                  opacity: 1;
                }
              }
            }
          }
        }
      }
    }
`
);

function SidebarMenu() {
  const { closeSidebar } = useContext(SidebarContext);
  const context = useContext(UserContext);
  const { currentUser, setUser } = context;
  const {
    accessToSalimax,
    accessToSodistore,
    accessToSalidomo,
    accessToSodiohome,
    accessToSodistoreGrid,
    accessToSodistorePro
  } = useContext(ProductIdContext);

  return (
    <>
      <MenuWrapper>
        <List
          component="div"
          subheader={
            <ListSubheader component="div" disableSticky>
              Products
            </ListSubheader>
          }
        >
          <SubMenuWrapper>
            {accessToSodiohome && (
              <List component="div">
                <ListItem component="div">
                  <Button
                    disableRipple
                    component={RouterLink}
                    onClick={closeSidebar}
                    to="/sodiohome_installations"
                    startIcon={<BrightnessLowTwoToneIcon />}
                  >
                    <Box sx={{ marginTop: '3px' }}>
                      <FormattedMessage
                        id="sodiohome"
                        defaultMessage="Sodistore Home"
                      />
                    </Box>
                  </Button>
                </ListItem>
              </List>
            )}

            {accessToSodistorePro && (
              <List component="div">
                <ListItem component="div">
                  <Button
                    disableRipple
                    component={RouterLink}
                    onClick={closeSidebar}
                    to="/sodistorepro_installations"
                    startIcon={<BrightnessLowTwoToneIcon />}
                  >
                    <Box sx={{ marginTop: '3px' }}>
                      <FormattedMessage
                        id="sodistorepro"
                        defaultMessage="Sodistore Pro"
                      />
                    </Box>
                  </Button>
                </ListItem>
              </List>
            )}

            {accessToSodistoreGrid && (
              <List component="div">
                <ListItem component="div">
                  <Button
                    disableRipple
                    component={RouterLink}
                    onClick={closeSidebar}
                    to="/sodistoregrid_installations"
                    startIcon={<BrightnessLowTwoToneIcon />}
                  >
                    <Box sx={{ marginTop: '3px' }}>
                      <FormattedMessage
                        id="sodistoregrid"
                        defaultMessage="Sodistore Grid"
                      />
                    </Box>
                  </Button>
                </ListItem>
              </List>
            )}

            {accessToSodistore && (
              <List component="div">
                <ListItem component="div">
                  <Button
                    disableRipple
                    component={RouterLink}
                    onClick={closeSidebar}
                    to="/sodistore_installations"
                    startIcon={<BrightnessLowTwoToneIcon />}
                  >
                    <Box sx={{ marginTop: '3px' }}>
                      <FormattedMessage
                        id="sodistore"
                        defaultMessage="Sodistore Max"
                      />
                    </Box>
                  </Button>
                </ListItem>
              </List>
            )}

            {accessToSalimax && (
              <List component="div">
                <ListItem component="div">
                  <Button
                    disableRipple
                    component={RouterLink}
                    onClick={closeSidebar}
                    to="/installations"
                    startIcon={<BrightnessLowTwoToneIcon />}
                  >
                    <Box sx={{ marginTop: '3px' }}>
                      <FormattedMessage id="salimax" defaultMessage="Salimax" />
                    </Box>
                  </Button>
                </ListItem>
              </List>
            )}

            {accessToSalidomo && (
              <List component="div">
                <ListItem component="div">
                  <Button
                    disableRipple
                    component={RouterLink}
                    onClick={closeSidebar}
                    to="/salidomo_installations"
                    startIcon={<BrightnessLowTwoToneIcon />}
                  >
                    <Box sx={{ marginTop: '3px' }}>
                      <FormattedMessage
                        id="salidomo"
                        defaultMessage="Salidomo"
                      />
                    </Box>
                  </Button>
                </ListItem>
              </List>
            )}
          </SubMenuWrapper>
        </List>

        {currentUser.userType == UserType.admin && (
          <List
            component="div"
            subheader={
              <ListSubheader component="div" disableSticky>
                Management
              </ListSubheader>
            }
          >
            <SubMenuWrapper>
              <List component="div">
                <ListItem component="div">
                  <Button
                    disableRipple
                    component={RouterLink}
                    onClick={closeSidebar}
                    to="/users"
                    startIcon={<TableChartTwoToneIcon />}
                  >
                    <FormattedMessage id="users" defaultMessage="Users" />
                  </Button>
                </ListItem>
              </List>
              <List component="div">
                <ListItem component="div">
                  <Button
                    disableRipple
                    component={RouterLink}
                    onClick={closeSidebar}
                    to="/tickets"
                    startIcon={<ConfirmationNumberTwoToneIcon />}
                  >
                    <FormattedMessage id="tickets" defaultMessage="Tickets" />
                  </Button>
                </ListItem>
              </List>
            </SubMenuWrapper>
          </List>
        )}
      </MenuWrapper>
    </>
  );
}

export default SidebarMenu;
